import { toast } from 'sonner';
import React, { useMemo, useState, useEffect } from 'react';

import { Box, Card, Button, Typography } from '@mui/material';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';
import { type RootState } from 'src/redux/store';

import { useChannelContext } from '../../hooks';

export function EmailStep5() {
  const { t } = useTranslate('translation');
  const { state, onChangeStep } = useChannelContext();
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const { loginApiData } = useAppSelector((state: RootState) => state.auth);

  const [websiteDetails, setWebsiteDetails] = useState({
    tenantId: '',
    webToken: '9HYgwfs6ZH8v1oD8bKqtZaX1',
    authToken: '',
    BASE_URL: '',
    welcomeTitle: '',
    welcomeTagline: '',
    widgetColor: '#000',
  });

  useEffect(() => {
    setWebsiteDetails({
      tenantId: state.channelCreatedResponse.channel.tenantId,
      authToken: loginApiData && 'token' in loginApiData ? loginApiData.token : '',
      webToken: state.channelCreatedResponse.config.websiteToken,
      BASE_URL: state.channelCreatedResponse.config.domainUrl,
      welcomeTagline: state.channelCreatedResponse.config.welcomeTagline || '',
      welcomeTitle: state.channelCreatedResponse.config.welcomeTitle || '',
      widgetColor: state.channelCreatedResponse.config.widgetColor || '#000',
    });
  }, [state.channelCreatedResponse, loginApiData]);

  const codeString = useMemo(() => {
    const rawScript = `<script>
    (function(d, t) {
      var g = d.createElement(t),
          s = d.getElementsByTagName(t)[0];
      g.src = '${window.location.origin}/sdk/chat-agent.js';
      g.defer = true;
      g.async = true;
      s.parentNode.insertBefore(g, s);
      g.onload = function() {
        window.liveChatSDK.run({
          tenantId: '${websiteDetails.tenantId}',
          websiteToken: '${websiteDetails.webToken}',
          welcomeTitle: '${websiteDetails.welcomeTitle}',
          welcomeTagline: '${websiteDetails.welcomeTagline}',
          widgetColor: '${websiteDetails.widgetColor}'
        });
      };
    })(document, 'script');
  </script>`;

    // Encode the full script using Base64
    const encoded = btoa(unescape(encodeURIComponent(rawScript)));

    // Inject decode + execution logic
    return `<script>
    (function(){
      const script = decodeURIComponent(escape(atob("${encoded}")));
      document.write(script);
    })();
  </script>`;
  }, [websiteDetails]);

  const copyText = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  };

  const copyTextHandler = () => {
    copyText(codeString);
    toast.success(t('SCRIPT_COPIED_SUCCESS'));
  };

  return (
    <Box
      sx={{
        height: '90%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {state?.channelType !== 'LIVE_CHAT' && (
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          {t('START_FORWARDING_EMAILS')}
        </Typography>
      )}
      {state?.channelType === 'LIVE_CHAT' && (
        <Box sx={{ width: '60%', textAlign: 'center', margin: '0 auto' }}>
          <Typography variant="h3" sx={{ textAlign: 'center', mb: 3 }}>
            {t('CHANNEL_CREATED_SUCCESSFULLY')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            {t('LIVE_CHAT_MESSAGE')}
          </Typography>
        </Box>
      )}

      <Card
        sx={{
          m: 2,
          px: 4,
          py: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          marginTop: '16px',
        }}
      >
        <Box sx={{ flex: 1, overflowX: 'auto', width: '800px' }}>
          <Typography variant="body1" sx={{ color: 'text.disabled' }}>
            <Box
              sx={{
                position: 'relative',
                background: '#f5f5f5',
                padding: '1rem',
                borderRadius: '8px',
                overflowX: 'auto',
                fontSize: '0.75rem',
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={copyTextHandler}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  fontSize: '0.7rem',
                  px: 1.5,
                  py: 0.25,
                  textTransform: 'uppercase',
                }}
              >
                {t('COPY')}
              </Button>
              <pre
                style={{
                  margin: 0,
                  width: '100%',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                <code>{codeString}</code>
              </pre>
            </Box>
          </Typography>
        </Box>
      </Card>

      {state?.channelType !== 'LIVE_CHAT' && (
        <Button size="small" sx={{ py: 2 }} variant="outlined" onClick={() => {}}>
          {t('MORE_SETTINGS')}
        </Button>
      )}

      {state?.channelType === 'LIVE_CHAT' && (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box>
            <Button
              fullWidth
              size="small"
              type="submit"
              variant="contained"
              onClick={() => onChangeStep('next')}
            >
              {t('FINISH')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
